import {styled} from '@mui/material/styles'
import {Dialog, DialogTitle, DialogContent, Box, Typography, Chip, Switch, IconButton} from '@mui/material'

const getBaseChipStyles = (theme) => ({
  color: theme.palette.hpBlack,
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.captionLarge?.fontSize,
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 1.62,
  height: theme.spacing(3.375),
  borderRadius: theme.shape.borderRadius,
  border: 'none',
  '& .MuiChip-label': {
    padding: theme.spacing(0.5, 1)
  }
})

const flexColumnStyles = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: 0
}

const flexRowStyles = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: 0
}

const getTypographyBase = (theme) => ({
  fontFamily: theme.typography.fontFamily,
  letterSpacing: theme.typography.body2?.letterSpacing,
  lineHeight: 1.5
})

const getLabelStyles = (theme) => ({
  ...getTypographyBase(theme),
  fontSize: theme.typography.caption?.fontSize,
  fontWeight: theme.typography.fontWeightBold,
  color: theme.palette.hpBlack
})

const getValueStyles = (theme) => ({
  ...getTypographyBase(theme),
  fontSize: theme.typography.caption?.fontSize,
  fontWeight: theme.typography.fontWeightRegular,
  color: theme.palette.hpAccentBlack
})

export const StyledDialog = styled(Dialog)(({theme}) => ({
  '& .MuiDialog-paper': {
    width: theme.spacing(87.75),
    maxWidth: theme.spacing(87.75),
    maxHeight: '90vh',
    borderRadius: theme.shape.borderRadius,
    margin: 'auto',
    background: theme.palette.hpWhite,
    boxShadow: theme.shadows[4]
  }
}))

export const StyledDialogTitle = styled(DialogTitle)(({theme}) => ({
  ...flexRowStyles,
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3.25),
  borderBottom: `1px solid ${theme.palette.hpBaseLight}`,
  backgroundColor: theme.palette.hpWhite
}))

export const StyledDialogContent = styled(DialogContent)(({theme}) => ({
  padding: theme.spacing(3, 3.25),
  backgroundColor: theme.palette.hpWhite,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  '&.MuiDialogContent-dividers': {
    borderTop: 'none',
    borderBottom: 'none'
  }
}))

export const StyledTitleText = styled(Typography)(({theme}) => ({
  ...getTypographyBase(theme),
  fontSize: theme.typography.kpiMedium?.fontSize,
  fontWeight: theme.typography.fontWeightBold,
  color: theme.palette.hpBlack,
  lineHeight: 1.5,
  display: 'flex',
  alignItems: 'center'
}))

export const StyledInfoLabel = styled(Typography)(({theme}) => getLabelStyles(theme))

export const StyledInfoValue = styled(Typography)(({theme}) => getValueStyles(theme))

export const StyledToggleLabel = styled(Typography)(({theme}) => ({
  ...getValueStyles(theme),
  whiteSpace: 'nowrap'
}))

export const StyledSectionTitle = styled(Typography)(({theme}) => ({
  ...getTypographyBase(theme),
  fontSize: theme.typography.body1?.fontSize,
  fontWeight: theme.typography.fontWeightSemibold,
  color: theme.palette.hpAccentBlack,
  marginBottom: theme.spacing(0)
}))

export const StyledSubSectionTitle = styled(Typography)(({theme}) => ({
  ...getLabelStyles(theme),
  marginBottom: theme.spacing(1)
}))

export const StyledCategoryTitle = styled(Typography)(({theme}) => getLabelStyles(theme))

export const StyledNoDataText = styled(Typography)(({theme}) => ({
  fontSize: theme.typography.captionLarge?.fontSize,
  color: theme.palette.hpBase,
  fontStyle: 'italic'
}))

export const StyledCloseButton = styled(IconButton)(({theme}) => ({
  color: theme.palette.action.active,
  padding: theme.spacing(0.5),
  width: theme.spacing(3),
  height: theme.spacing(3),
  '&:hover': {
    backgroundColor: 'transparent'
  }
}))

export const StyledHeaderRow = styled(Box)(({theme}) => ({
  ...flexRowStyles,
  justifyContent: 'space-between',
  width: '100%',
  height: theme.spacing(3),
  marginBottom: theme.spacing(0)
}))

export const StyledToggleContainer = styled(Box)(({theme}) => ({
  ...flexRowStyles,
  gap: theme.spacing(1.5),
  height: theme.spacing(3)
}))

export const StyledProjectInfoRow = styled(Box)(({theme}) => ({
  ...flexRowStyles,
  gap: theme.spacing(10),
  height: theme.spacing(6)
}))

export const StyledProjectInfoItem = styled(Box)(() => flexColumnStyles)

export const StyledChipContainer = styled(Box)(({theme}) => ({
  ...flexRowStyles,
  flexWrap: 'wrap',
  alignContent: 'flex-start',
  gap: theme.spacing(1)
}))

export const StyledCategoryRow = styled(Box)(({theme}) => ({
  ...flexColumnStyles,
  gap: theme.spacing(1)
}))

export const StyledPermissionSection = styled(Box)(({theme}) => ({
  ...flexColumnStyles,
  gap: theme.spacing(1)
}))

export const StyledDivider = styled(Box)(({theme}) => ({
  width: '100%',
  height: 0,
  border: `1px solid ${theme.palette.hpBaseLight}`
}))

export const StyledChip = styled(Chip)(({theme}) => ({
  ...getBaseChipStyles(theme),
  backgroundColor: theme.palette.hpBaseLighter
}))

export const StyledFlagChip = StyledChip

export const StyledInsertionChip = styled(Chip)(({theme}) => ({
  ...getBaseChipStyles(theme),
  backgroundColor: theme.palette.success.light,
  '& .MuiChip-label': {
    padding: theme.spacing(0.5, 1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  }
}))

export const StyledDeletionChip = styled(Chip)(({theme}) => ({
  ...getBaseChipStyles(theme),
  backgroundColor: theme.palette.error.light,
  '& .MuiChip-label': {
    padding: theme.spacing(0.5, 1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  }
}))

export const StyledChangedFlagChip = styled(Chip)(({theme}) => ({
  ...getBaseChipStyles(theme),
  backgroundColor: theme.palette.success.light
}))

export const StyledDateInfo = styled(Box)(({theme}) => ({
  ...flexRowStyles,
  height: theme.spacing(3)
}))

export const StyledDateItem = styled(Box)(() => flexRowStyles)

export const StyledDateLabel = styled(Typography)(({theme}) => ({
  ...getTypographyBase(theme),
  fontStyle: 'italic',
  fontSize: theme.typography.captionLarge?.fontSize,
  fontWeight: theme.typography.fontWeightRegular,
  color: theme.palette.hpBlack,
  whiteSpace: 'nowrap',
  marginRight: theme.spacing(0.5)
}))

export const StyledDateValue = styled(Typography)(({theme}) => ({
  ...getTypographyBase(theme),
  fontStyle: 'italic',
  fontSize: theme.typography.captionLarge?.fontSize,
  fontWeight: theme.typography.fontWeightRegular,
  color: theme.palette.hpBlack
}))

export const StyledLoadingContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: theme.spacing(25)
}))

export const StyledErrorText = styled(Typography)(({theme}) => ({
  color: theme.palette.hpAccentRed,
  textAlign: 'center',
  padding: theme.spacing(2),
  fontSize: theme.typography.body1?.fontSize
}))

export const StyledSwitch = styled(Switch)(({theme}) => ({
  width: theme.spacing(4.375),
  height: theme.spacing(2),
  padding: 0,
  overflow: 'visible',
  '&.Mui-disabled': {
    cursor: 'not-allowed',
    pointerEvents: 'auto',
    '& .MuiSwitch-switchBase': {
      pointerEvents: 'none'
    },
    '& .MuiSwitch-track': {
      cursor: 'not-allowed',
      pointerEvents: 'auto'
    }
  },
  '& .MuiSwitch-switchBase': {
    padding: 1,
    '&.Mui-checked': {
      transform: `translateX(${theme.spacing(2.375)})`,
      color: theme.palette.hpWhite,
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.hpAccentGreenVivid,
        opacity: 1,
        border: 'none',
        '&:before': {
          content: '"on"',
          position: 'absolute',
          left: theme.spacing(0.75),
          top: '50%',
          transform: 'translateY(-50%)',
          fontFamily: theme.typography.fontFamily,
          fontSize: theme.spacing(1),
          fontWeight: theme.typography.fontWeightBold,
          letterSpacing: theme.spacing(0.09),
          color: theme.palette.hpWhite
        }
      }
    },
    '&.Mui-disabled': {
      '& + .MuiSwitch-track': {
        opacity: 0.5
      }
    }
  },
  '& .MuiSwitch-thumb': {
    width: theme.spacing(1.5),
    height: theme.spacing(1.5),
    boxShadow: 'none',
    backgroundColor: theme.palette.hpWhite,
    marginTop: theme.spacing(0.125)
  },
  '& .MuiSwitch-track': {
    borderRadius: theme.spacing(0.9375),
    backgroundColor: theme.palette.hpBaseDark,
    opacity: 1,
    position: 'relative',
    '&:after': {
      content: '"off"',
      position: 'absolute',
      right: theme.spacing(0.375),
      top: '50%',
      transform: 'translateY(-50%)',
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.spacing(1),
      fontWeight: theme.typography.fontWeightBold,
      letterSpacing: theme.spacing(0.09),
      color: theme.palette.hpWhite
    }
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    '&:after': {
      content: 'none'
    }
  }
}))
